import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { IconButton } from '@xing-com/button';
import { IconBookmarkFilled, IconNotificationFilled } from '@xing-com/icons';

import { useJobCardActions } from '../actions/index';
import { CardModal } from '../card-modal';
import { useJobCardTracking } from '../cards/job-card/use-job-card-tracking';
import { useSearchAndShuffleContext } from '../context';
import { SavedCardData } from '../types';

import {
  Logo,
  MainArea,
  JobTitle,
  Location,
  CompanyName,
  CardType,
  Container,
} from './saved-job-card.style';

export type SavedJobCardProps = {
  card: SavedCardData;
};

export const SavedJobCard: React.FC<SavedJobCardProps> = ({ card }) => {
  const intl = useIntl();
  const [showModal, setShowModal] = useState(false);
  const { trackYourJobsOpenAvailableJob } = useJobCardTracking();
  const isAvailableJob = card?.type === 'AvailableJob';
  const { jobCardDeleteBookmark, jobCardDeleteSearchAlert } =
    useJobCardActions();
  const { setDeletingSavedCards } = useSearchAndShuffleContext();

  const openCardHandler = (e: React.MouseEvent): void => {
    const url =
      card.type === 'AvailableJob' && card.data.jobSlug
        ? generatePath('/jobs/:jobSlug', { jobSlug: card.data.jobSlug })
        : null;
    if (url) {
      e.preventDefault();
      e.stopPropagation();
      window.open(url, '_blank');
      trackYourJobsOpenAvailableJob({
        itemUrn: card.data.urn || '',
        trackingToken: '',
      });
    }
    if (card.type === 'TakenJob') {
      setShowModal(true);
    }
  };

  if (card.type !== 'AvailableJob' && card.type !== 'TakenJob') {
    return null;
  }

  return (
    <>
      <Container
        data-qa="saved-job-card"
        onClick={(e: React.MouseEvent) => openCardHandler(e)}
      >
        <Logo
          src={card?.data?.companyLogoUrl || undefined}
          size="xsmall"
          type="company"
          alt={`${card?.data?.companyName}`}
        />
        <MainArea>
          <JobTitle size="small" fontWeight="bold" lineHeight="dense">
            {card?.data?.title}
          </JobTitle>
          <Location size="small" lineHeight="dense">
            {card?.data?.location}
          </Location>
          <CompanyName size="small" lineHeight="dense">
            {card?.data?.companyName}
          </CompanyName>
          {!isAvailableJob && <CardType type={card.type} condensed={true} />}
        </MainArea>
        <IconButton
          size="small"
          icon={isAvailableJob ? IconBookmarkFilled : IconNotificationFilled}
          aria-label={intl.formatMessage({
            id: 'DREAM_MACHINE_ACTIVITY_MENU_VIEW_DELETE_JOB',
          })}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            if (isAvailableJob) {
              jobCardDeleteBookmark(card.data.id, card.data.urn);
            } else if (card.data.searchAlertId) {
              jobCardDeleteSearchAlert(
                card.data.id,
                card.data.searchAlertId,
                card.data.urn
              );
            }
          }}
        />
      </Container>
      <CardModal
        cardId={card.data.id}
        show={showModal}
        setShow={setShowModal}
        onAlertIdUpdate={(_, newAlertId) => {
          if (newAlertId === undefined) {
            setDeletingSavedCards((prev) => [...prev, card.data.id]);
          }
        }}
      />
    </>
  );
};
